import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IconControl, Loading } from "spark-core-dx/components";
import { useGenericManager } from "spark-core-dx/hooks";
import AIPanel from "../../../components/AIChat/AIPanel";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import EntityFieldMapDesigner from "../components/entityFieldMapDesigner/EntityFieldMapDesigner";
import { hardRedirectTo } from "./screenNavigation";
import "./entityFieldMapDesignerManage.scss";
import {
  aggregateFieldMapsForSource,
  asArray,
  buildEmptyFieldMap,
  dedupeTransforms,
  EMPTY_GUID,
  ensureUniqueMapItemIds,
  firstId,
  normalizeMapItem,
  normalizeEntityObject,
  normalizeFieldMap,
  normalizeLoopMap,
  normalizeSourceObject,
  toLoopSavePayload,
  toSavePayloads,
} from "../components/entityFieldMapDesigner/utils";

const parseSourceIds = (searchParams) => {
  const single =
    searchParams.get("sourceObjectId") || searchParams.get("soId") || searchParams.get("sourceId") || searchParams.get("sourceobjectid");
  const multi = searchParams.get("sourceObjectIds") || searchParams.get("soIds");
  if (multi) {
    return multi
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  }
  if (single) return [single];
  return [];
};

const getCurrentRelativeUrl = () => {
  if (typeof window === "undefined" || !window.location) return "/";
  return `${window.location.pathname}${window.location.search}${window.location.hash}` || "/";
};

const getRawBackUrl = (searchParams) =>
  searchParams.get("backUrl") ||
  searchParams.get("backurl") ||
  searchParams.get("returnUrl") ||
  searchParams.get("returnurl") ||
  "";

const resolveBackUrl = (searchParams) => {
  const rawBackUrl = getRawBackUrl(searchParams);

  if (!rawBackUrl) return getCurrentRelativeUrl();

  let decodedBackUrl = rawBackUrl;
  try {
    decodedBackUrl = decodeURIComponent(rawBackUrl);
  } catch {
    decodedBackUrl = rawBackUrl;
  }

  if (decodedBackUrl.startsWith("/") && !decodedBackUrl.startsWith("//")) {
    return decodedBackUrl;
  }

  try {
    const url = new URL(decodedBackUrl, window.location.origin);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    // Fall through to current page fallback.
  }

  return getCurrentRelativeUrl();
};

const hasExplicitBackUrl = (searchParams) => Boolean(getRawBackUrl(searchParams));

const firstItem = (value) => {
  if (!value) return null;
  if (typeof value?.first === "function") return value.first();
  return asArray(value)[0] || null;
};

const readAiResultItem = (result) => {
  if (!result) return null;
  return result?.Value || firstItem(result?.Items) || firstItem(result?.Value?.Items) || null;
};

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const readPanelValue = (item, value) => {
  const direct = parseMaybeJson(value);
  if (direct && typeof direct === "object") return direct;
  const output = parseMaybeJson(item?.Output || item?.output);
  if (output && typeof output === "object") return output;
  return direct;
};

const flattenAiMapsToItems = (resultItem, entityObjectId) => {
  const maps = asArray(resultItem?.Maps || resultItem?.maps);
  if (maps.length === 0) return [];
  return maps
    .map((map) => normalizeFieldMap(map, entityObjectId))
    .flatMap((map) => asArray(map?.items))
    .filter(Boolean);
};

const operationErrorMessage = (result, fallback) => {
  return (
    result?.DisplayMessage ||
    result?.Message?.DisplayMessage ||
    result?.Message?.Content ||
    asArray(result?.MessageDetails)[0] ||
    result?.Exception?.Message ||
    fallback
  );
};

const tryGetLegacyDti = (payload) => {
  const value = Array.isArray(payload) ? payload[0] : payload;
  if (!value || typeof value !== "object") return null;
  if (!value.EntityObject || !Array.isArray(value.FieldMaps)) return null;
  return value;
};

const buildMapsFromLegacyDti = (legacyDti, entityId) => {
  const sourceObjects = asArray(legacyDti?.SourceObjects).map((x) => normalizeSourceObject(x));
  const mapsBySourceId = {};
  const parentFieldMapsByEntityFieldId = {};

  sourceObjects.forEach((source) => {
    if (!source?.id) return;
    mapsBySourceId[source.id] = buildEmptyFieldMap(entityId, source.id);
  });

  asArray(legacyDti?.FieldMaps).forEach((entityFieldMap) => {
    if (entityFieldMap?.IsDeleted === true || entityFieldMap?.IsActive === false) {
      return;
    }
    const entityFieldId = entityFieldMap?.EntityObjectFieldId || entityFieldMap?.entityFieldId;
    if (!entityFieldId) return;
    parentFieldMapsByEntityFieldId[entityFieldId] = entityFieldMap;

    asArray(entityFieldMap?.FieldMap).forEach((sourceMap) => {
      const sourceObjectId = sourceMap?.SourceObjectId || sourceMap?.sourceObjectId;
      if (!sourceObjectId) return;

      const normalizedItem = normalizeMapItem(sourceMap, entityFieldId);
      if (
        normalizedItem?.isDeleted === true ||
        !normalizedItem?.entityFieldId ||
        (!normalizedItem?.sourceFieldId && normalizedItem?.isConstant !== true)
      ) {
        return;
      }

      mapsBySourceId[sourceObjectId] ??= buildEmptyFieldMap(entityId, sourceObjectId);
      mapsBySourceId[sourceObjectId].items.push({
        ...normalizedItem,
        sourceObjectId,
        id: normalizedItem.id || firstId(sourceMap) || `item_${Math.random().toString(36).slice(2)}`,
        raw: sourceMap,
      });
    });
  });

  Object.keys(mapsBySourceId).forEach((sourceId) => {
    mapsBySourceId[sourceId].items = ensureUniqueMapItemIds(mapsBySourceId[sourceId].items);
    mapsBySourceId[sourceId].parentFieldMapsByEntityFieldId = { ...parentFieldMapsByEntityFieldId };
  });

  return { sourceObjects, mapsBySourceId };
};

const groupLoopMapsBySource = (rawLoops, entityId) => {
  const grouped = {};
  asArray(rawLoops)
    .map((loop) => normalizeLoopMap(loop, entityId, loop?.SourceObjectId || loop?.sourceObjectId || null))
    .filter((loop) => loop?.sourceObjectId && loop?.isDeleted !== true && loop?.isActive !== false)
    .forEach((loop) => {
      grouped[loop.sourceObjectId] ??= [];
      grouped[loop.sourceObjectId].push(loop);
    });
  return grouped;
};

const toEntityFieldSaveModel = (field, sequence) => {
  const raw = field?.raw || {};
  const fieldId = field?.id || raw?._id || raw?.UniqueId || raw?.Id || raw?.EntityObjectFieldId || null;
  return {
    ...raw,
    _id: raw?._id || raw?.UniqueId || raw?.Id || fieldId,
    EntityObjectFieldId: raw?.EntityObjectFieldId || fieldId,
    FieldName: field?.fieldName ?? raw?.FieldName ?? raw?.Name ?? raw?.SystemName ?? "",
    DisplayName: field?.displayName ?? raw?.DisplayName ?? raw?.Label ?? "",
    DataTypeId: field?.dataTypeId ?? raw?.DataTypeId ?? raw?.DataType?._id ?? raw?.DataType?.UniqueId ?? raw?.DataType?.Id ?? raw?.DataType,
    DataTypeName: field?.dataTypeName ?? raw?.DataTypeName ?? raw?.DataType?.DisplayName ?? raw?.DataType?.Name ?? "",
    Description: field?.description ?? raw?.Description ?? raw?.HelpText ?? "",
    AIPromptContext: field?.aiPromptContext ?? raw?.AIPromptContext ?? "",
    Format: field?.format ?? raw?.Format ?? "",
    Sequence: typeof raw?.Sequence === "number" ? raw.Sequence : sequence + 1,
    IsActive: field?.isActive !== false,
    IsDeleted: raw?.IsDeleted === true,
  };
};

const EntityFieldMapDesignerManage = () => {
  const itemService = useMemo(() => new ItemServiceFactory(), []);
  const params = useParams();
  const eoId = params?.eoId || params?.id;
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const navigate = useNavigate();
  const backUrl = useMemo(() => resolveBackUrl(searchParams), [searchParamsKey, searchParams]);
  const useHardBackRedirect = useMemo(() => hasExplicitBackUrl(searchParams), [searchParamsKey, searchParams]);

  const allowMultiSource = String(searchParams.get("allowMultiSource") || "false").toLowerCase() === "true";
  const aiFieldOp = searchParams.get("aiFieldOp") || "AIEntityFieldMap";
  const aiTransformOp = searchParams.get("aiTransformOp") || "AIBuildJSTransform";
  const itemServiceEnum = itemService?.ItemServiceEnum || {};

  const entityObjectMgr = useGenericManager(itemServiceEnum.EntityObject || "EntityObject");
  const sourceObjectMgr = useGenericManager(itemServiceEnum.SourceObject || "SourceObject");
  const entityFieldMapMgr = useGenericManager(itemServiceEnum.EntityObjectFieldMap || "EntityObjectFieldMap");
  const entityLoopMapMgr = useGenericManager(itemServiceEnum.EntityObjectLoopMap || "EntityObjectLoopMap", false);
  const dataTypeMgr = useGenericManager(itemServiceEnum.DataType || "DataType");
  const transformScriptMgr = useGenericManager(itemServiceEnum.TransformScript || "TransformScript");
  const chatSessionMgr = useGenericManager(itemServiceEnum.AIChatSession || "AIChatSession");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aiStatus, setAiStatus] = useState("");
  const [entityObject, setEntityObject] = useState(null);
  const [sourceObjects, setSourceObjects] = useState([]);
  const [initialMapsBySourceId, setInitialMapsBySourceId] = useState({});
  const [initialLoopMapsBySourceId, setInitialLoopMapsBySourceId] = useState({});
  const [dataTypes, setDataTypes] = useState([]);
  const [transformCatalog, setTransformCatalog] = useState([]);
  const [lookupSourceObjects, setLookupSourceObjects] = useState([]);
  const [lookupEntityObjects, setLookupEntityObjects] = useState([]);
  const [selectedChatSession, setSelectedChatSession] = useState(null);
  const [aiMappingContext, setAiMappingContext] = useState(null);
  const [aiDraftUpdate, setAiDraftUpdate] = useState(null);
  const [fieldItemMetadata, setFieldItemMetadata] = useState(null);
  const [entityObjectItemMetadata, setEntityObjectItemMetadata] = useState(null);
  const [sourceObjectFieldMapItemMetadata, setSourceObjectFieldMapItemMetadata] = useState(null);
  const [sourceObjectFieldTransformScriptItemMetadata, setSourceObjectFieldTransformScriptItemMetadata] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const loadMappingChatSession = async (referenceId) => {
          if (!referenceId) return null;
          try {
            const chatSessionResult = await chatSessionMgr.Query({
              model: "x => x.ReferenceId == referenceId && x.ReferenceItemName == referenceItemName && x.IsDeleted == false && x.IsActive == true",
              params: { referenceId, referenceItemName: "EntityFieldMapping" },
            });
            return firstItem(chatSessionResult?.Items) || null;
          } catch {
            return null;
          }
        };

        const loadTransformCatalog = async () => {
          try {
            const transformResult = await transformScriptMgr.Query("x => x.IsDeleted == false && x.IsActive == true");
            return dedupeTransforms(
              asArray(transformResult?.Items)
              .map((x) => {
                const id = x?._id || x?.UniqueId || x?.Id || x?.id;
                const key = id || x?.Name || x?.DisplayName;
                return {
                  id,
                  key,
                  name: x?.DisplayName || x?.Name || String(key || ""),
                  displayName: x?.DisplayName || x?.Name || String(key || ""),
                  description: x?.Description || "",
                  group: x?.Group || x?.group || "Other",
                  code: x?.Script || x?.Code || "",
                  sequence: Number.isFinite(Number(x?.Sequence)) ? Number(x.Sequence) : 9999,
                };
              })
              .filter((x) => Boolean(x?.key))
            )
              .sort((a, b) => {
                if (a.sequence !== b.sequence) return a.sequence - b.sequence;
                return String(a.displayName || "").localeCompare(String(b.displayName || ""));
              });
          } catch {
            return [];
          }
        };

        const loadLookupEntityObjects = async (normalizedEntity) => {
          const currentEntity = normalizedEntity || null;
          if (!currentEntity?.id) return [];

          try {
            const clientId = currentEntity?.raw?.ClientId || currentEntity?.raw?.clientId || null;
            const queryResult = clientId
              ? await entityObjectMgr.Query({
                  model: "x => x.IsActive == true && x.IsDeleted == false && x.ClientId == clientId",
                  params: { clientId },
                })
              : await entityObjectMgr.Query("x => x.IsActive == true && x.IsDeleted == false");

            const normalized = asArray(queryResult?.Items)
              .map((item) => normalizeEntityObject(item))
              .filter((item) => Boolean(item?.id));

            const deduped = new Map(normalized.map((item) => [item.id, item]));
            deduped.set(currentEntity.id, currentEntity);
            return Array.from(deduped.values()).sort((left, right) =>
              String(left?.displayName || left?.objectName || "").localeCompare(String(right?.displayName || right?.objectName || ""))
            );
          } catch {
            return [currentEntity];
          }
        };

        const loadLookupSourceObjects = async (normalizedEntity, existingSources = []) => {
          const clientId = normalizedEntity?.raw?.ClientId || normalizedEntity?.raw?.clientId || null;

          try {
            const queryResult = clientId
              ? await sourceObjectMgr.Query({
                  model: "x => x.IsActive == true && x.IsDeleted == false && x.ClientId == clientId",
                  params: { clientId },
                })
              : await sourceObjectMgr.Query("x => x.IsActive == true && x.IsDeleted == false");

            const normalized = asArray(queryResult?.Items)
              .map((item) => normalizeSourceObject(item))
              .filter((item) => Boolean(item?.id));

            const deduped = new Map(normalized.map((item) => [item.id, item]));
            asArray(existingSources).forEach((item) => {
              if (item?.id) deduped.set(item.id, item);
            });

            return Array.from(deduped.values()).sort((left, right) =>
              String(left?.displayName || left?.objectName || "").localeCompare(String(right?.displayName || right?.objectName || ""))
            );
          } catch {
            return asArray(existingSources);
          }
        };

        const loadLoopMapsBySource = async (entityId) => {
          if (!entityId) return {};
          try {
            const loopResult = await entityLoopMapMgr.Query({
              model: "x => x.EntityObjectId == entityObjectId && x.IsActive == true && x.IsDeleted == false",
              params: { entityObjectId: entityId },
            });
            if (loopResult?.Success === false) {
              const message = operationErrorMessage(loopResult, "Loop mappings could not be loaded.");
              setError(message);
              return {};
            }
            return groupLoopMapsBySource(loopResult?.Items, entityId);
          } catch (ex) {
            setError(ex?.message || "Loop mappings could not be loaded.");
            return {};
          }
        };

        const [entityMetadataResult, fieldMapMetadataResult] = await Promise.all([
          entityObjectMgr.GetMetadata({}),
          entityFieldMapMgr.GetMetadata({}),
        ]);
        const metadataItems = [
          ...asArray(entityMetadataResult?.Items),
          ...asArray(fieldMapMetadataResult?.Items),
        ];
        const entityFieldMetadata =
          metadataItems.find((x) => String(x?.Name || "").toLowerCase() === "entityobjectfield") ||
          metadataItems.find((x) => String(x?.Name || "").toLowerCase().includes("entityobjectfield")) ||
          null;
        const entityObjectMetadata =
          metadataItems.find((x) => String(x?.Name || "").toLowerCase() === "entityobject") ||
          metadataItems.find((x) => String(x?.Name || "").toLowerCase().includes("entityobject")) ||
          null;
        const sourceObjectFieldMapMetadata =
          metadataItems.find((x) => String(x?.Name || "").toLowerCase() === "sourceobjectfieldmap") ||
          metadataItems.find((x) => String(x?.Name || "").toLowerCase().includes("sourceobjectfieldmap")) ||
          null;
        const sourceObjectFieldTransformScriptMetadata =
          metadataItems.find((x) => String(x?.Name || "").toLowerCase() === "sourceobjectfieldtransformscript") ||
          metadataItems.find((x) => String(x?.Name || "").toLowerCase().includes("sourceobjectfieldtransformscript")) ||
          null;
        setFieldItemMetadata(entityFieldMetadata);
        setEntityObjectItemMetadata(entityObjectMetadata);
        setSourceObjectFieldMapItemMetadata(sourceObjectFieldMapMetadata);
        setSourceObjectFieldTransformScriptItemMetadata(sourceObjectFieldTransformScriptMetadata);

        const redirectRaw = sessionStorage.getItem("InternalRedirectData");
        if (redirectRaw) {
          const parsed = JSON.parse(redirectRaw);
          const legacyDti = tryGetLegacyDti(parsed);
          if (legacyDti) {
            const normalizedEntity = normalizeEntityObject(legacyDti.EntityObject);
            const { sourceObjects: legacySources, mapsBySourceId } = buildMapsFromLegacyDti(legacyDti, normalizedEntity.id || eoId);

            setEntityObject(normalizedEntity);
            setSourceObjects(legacySources);
            setInitialMapsBySourceId(mapsBySourceId);
            setInitialLoopMapsBySourceId(await loadLoopMapsBySource(normalizedEntity.id || eoId));
            setLookupSourceObjects(await loadLookupSourceObjects(normalizedEntity, legacySources));
            setLookupEntityObjects(await loadLookupEntityObjects(normalizedEntity));
            setSelectedChatSession(await loadMappingChatSession(normalizedEntity.id || eoId));
            sessionStorage.removeItem("InternalRedirectData");

            const dataTypeResult = await dataTypeMgr.Query("x => x.IsActive == true && x.IsDeleted == false");
            const normalizedDataTypes = asArray(dataTypeResult?.Items).map((x) => ({
              id: x?._id || x?.UniqueId || x?.Id || x?.id,
              name: x?.Name || x?.DisplayName || x?.DataTypeName,
              displayName: x?.DisplayName || x?.Name || x?.DataTypeName,
            }));
            setDataTypes(normalizedDataTypes);
            setTransformCatalog(await loadTransformCatalog());
            return;
          }
        }

        const mappingResult = await entityObjectMgr.RunOperation({ operationName: "EntityObjectFieldMapping", id: eoId });
        const mappingDti = tryGetLegacyDti(asArray(mappingResult?.Items)[0]);
        if (mappingDti) {
          const normalizedEntity = normalizeEntityObject(mappingDti.EntityObject);
          const { sourceObjects: legacySources, mapsBySourceId } = buildMapsFromLegacyDti(mappingDti, normalizedEntity.id || eoId);

          setEntityObject(normalizedEntity);
          setSourceObjects(legacySources);
          setInitialMapsBySourceId(mapsBySourceId);
          setInitialLoopMapsBySourceId(await loadLoopMapsBySource(normalizedEntity.id || eoId));
          setLookupSourceObjects(await loadLookupSourceObjects(normalizedEntity, legacySources));
          setLookupEntityObjects(await loadLookupEntityObjects(normalizedEntity));
          setSelectedChatSession(await loadMappingChatSession(normalizedEntity.id || eoId));

          const dataTypeResult = await dataTypeMgr.Query("x => x.IsActive == true && x.IsDeleted == false");
          const normalizedDataTypes = asArray(dataTypeResult?.Items).map((x) => ({
            id: x?._id || x?.UniqueId || x?.Id || x?.id,
            name: x?.Name || x?.DisplayName || x?.DataTypeName,
            displayName: x?.DisplayName || x?.Name || x?.DataTypeName,
          }));
          setDataTypes(normalizedDataTypes);
          setTransformCatalog(await loadTransformCatalog());
          return;
        }

        const entityResult = await entityObjectMgr.getItem(eoId);
        if (!entityResult?.Success || !entityResult?.Items?.length) {
          throw new Error("Entity object not found.");
        }
        const normalizedEntity = normalizeEntityObject(entityResult.Items.first());

        const mapsResult = await entityFieldMapMgr.Query({
          model: "x => x.EntityObjectId == entityObjectId && x.IsActive == true && x.IsDeleted == false",
          params: { entityObjectId: eoId },
        });
        const rawFieldMaps = asArray(mapsResult?.Items);
        const parentFieldMapsByEntityFieldId = {};
        rawFieldMaps.forEach((fieldMap) => {
          const entityFieldId = fieldMap?.EntityObjectFieldId || fieldMap?.entityFieldId;
          if (entityFieldId) parentFieldMapsByEntityFieldId[entityFieldId] = fieldMap;
        });

        let sourceIds = parseSourceIds(searchParams);
        if (sourceIds.length === 0) {
          sourceIds = Array.from(
            new Set(
              rawFieldMaps
                .flatMap((fieldMap) =>
                  asArray(fieldMap?.FieldMap)
                    .filter((sourceMap) => sourceMap?.IsDeleted !== true && sourceMap?.IsActive !== false)
                    .map((sourceMap) => sourceMap?.SourceObjectId || sourceMap?.sourceObjectId)
                    .filter((id) => id && id !== EMPTY_GUID)
                )
                .filter(Boolean)
            )
          );
        }
        if (sourceIds.length === 0) sourceIds = [];

        const sourceResults = await Promise.all(sourceIds.map((id) => sourceObjectMgr.getItem(id)));
        const normalizedSources = sourceResults
          .filter((r) => r?.Success && r?.Items?.length)
          .map((r) => normalizeSourceObject(r.Items.first()))
          .filter((x) => x.id);

        if (normalizedSources.length === 0) {
          throw new Error("Source objects could not be loaded.");
        }

        const mapsBySourceId = {};
        normalizedSources.forEach((source) => {
          mapsBySourceId[source.id] = aggregateFieldMapsForSource(
            rawFieldMaps,
            eoId,
            source.id,
            parentFieldMapsByEntityFieldId
          );
        });

        const dataTypeResult = await dataTypeMgr.Query("x => x.IsActive == true && x.IsDeleted == false");
        const normalizedDataTypes = asArray(dataTypeResult?.Items).map((x) => ({
          id: x?._id || x?.UniqueId || x?.Id || x?.id,
          name: x?.Name || x?.DisplayName || x?.DataTypeName,
          displayName: x?.DisplayName || x?.Name || x?.DataTypeName,
        }));

        const transforms = await loadTransformCatalog();

        setEntityObject(normalizedEntity);
        setSourceObjects(allowMultiSource ? normalizedSources : [normalizedSources[0]]);
        setInitialMapsBySourceId(mapsBySourceId);
        setInitialLoopMapsBySourceId(await loadLoopMapsBySource(normalizedEntity.id || eoId));
        setDataTypes(normalizedDataTypes);
        setTransformCatalog(transforms);
        setLookupSourceObjects(await loadLookupSourceObjects(normalizedEntity, normalizedSources));
        setLookupEntityObjects(await loadLookupEntityObjects(normalizedEntity));
        setSelectedChatSession(await loadMappingChatSession(normalizedEntity.id || eoId));
      } catch (err) {
        setError(err?.message || "Failed to load field map designer.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [eoId, allowMultiSource, searchParamsKey]);

  const handleAiContextChange = (context) => {
    setAiMappingContext(context);
  };

  const handleAiPanelUpdate = async (uiAction, item, value) => {
    const normalizedAction = String(uiAction || "").toLowerCase();
    if (normalizedAction !== "entityfieldmappingdraftupdated") return;

    const resultItem = readPanelValue(item, value);
    const normalized = flattenAiMapsToItems(resultItem, entityObject?.id);
    const sourceObjectId = resultItem?.SourceObjectId || resultItem?.sourceObjectId || aiMappingContext?.activeSourceId || aiMappingContext?.sourceObjectId;
    if (normalized.length > 0) {
      setAiDraftUpdate({
        version: Date.now(),
        sourceObjectId,
        items: normalized,
      });
      setAiStatus(resultItem?.Comment || resultItem?.comment || "AI updated the mapping draft. Review and save when ready.");
      return;
    }

    setAiStatus(resultItem?.Comment || resultItem?.comment || "AI did not return draft mapping changes.");
  };

  const handleAiSessionChange = (sessionId, session) => {
    if (session) {
      setSelectedChatSession(session);
      return;
    }
    if (sessionId) {
      setSelectedChatSession({
        _id: sessionId,
        ReferenceId: eoId,
        ReferenceItemName: "EntityFieldMapping",
      });
    }
  };

  const handleSaveMap = async (map, options = {}) => {
    const previousMapItems = asArray(options?.previousMap?.items);
    const currentIds = new Set(asArray(map?.items).map((x) => x?.id).filter(Boolean));
    const removedExistingItems = previousMapItems.filter(
      (x) =>
        x?.id &&
        !String(x.id).startsWith("item_") &&
        x?.raw?.IsDeleted !== true &&
        !currentIds.has(x.id)
    );
    const removedItemsToSoftDelete = removedExistingItems;

    const payloads = toSavePayloads(map, removedItemsToSoftDelete);
    const savedFieldMaps = [];
    const deleteOnlyPayloads = payloads.filter((payload) => {
      const childRows = asArray(payload?.FieldMap);
      return payload?._id && childRows.length > 0 && childRows.every((row) => row?.IsDeleted === true);
    });
    const upsertPayloads = payloads.filter((payload) => !deleteOnlyPayloads.includes(payload));

    for (const payload of deleteOnlyPayloads) {
      const result = await entityFieldMapMgr.deleteItem(payload._id, true);
      if (!result?.Success) return result;
    }

    for (const payload of upsertPayloads) {
      const result = await entityFieldMapMgr.saveItem(payload);
      if (!result?.Success) return result;
      const savedRaw = result?.Items?.first ? result.Items.first() : payload;
      if (savedRaw) savedFieldMaps.push(savedRaw);
    }

    const mergedParentFieldMaps = {
      ...(map?.parentFieldMapsByEntityFieldId || {}),
    };
    deleteOnlyPayloads.forEach((payload) => {
      const entityFieldId = payload?.EntityObjectFieldId;
      if (entityFieldId) delete mergedParentFieldMaps[entityFieldId];
    });
    savedFieldMaps.forEach((fieldMap) => {
      const entityFieldId = fieldMap?.EntityObjectFieldId || fieldMap?.entityFieldId;
      if (entityFieldId) mergedParentFieldMaps[entityFieldId] = fieldMap;
    });

    const normalizedSaved = aggregateFieldMapsForSource(
      savedFieldMaps,
      eoId,
      map?.sourceObjectId,
      mergedParentFieldMaps
    );

    return { Success: true, map: normalizedSaved };
  };

  const handleSaveLoops = async (sourceId, loops, options = {}) => {
    const currentLoops = asArray(loops);
    const currentIds = new Set(currentLoops.map((loop) => loop?.id).filter(Boolean));
    const removedLoops = asArray(options?.previousLoops).filter(
      (loop) => loop?.id && loop?.persistedId && !currentIds.has(loop.id)
    );
    const payloads = [
      ...currentLoops,
      ...removedLoops.map((loop) => ({ ...loop, isActive: false, isDeleted: true })),
    ].map((loop) =>
      toLoopSavePayload({
        ...loop,
        entityObjectId: loop?.entityObjectId || eoId,
        sourceObjectId: loop?.sourceObjectId || sourceId,
      })
    );

    const savedLoops = [];
    try {
      for (const payload of payloads) {
        const result = payload?._id && payload?.IsDeleted === true
          ? await entityLoopMapMgr.deleteItem(payload._id, true)
          : await entityLoopMapMgr.saveItem(payload);
        if (!result?.Success) {
          const message = operationErrorMessage(result, "Loop mappings could not be saved.");
          setError(message);
          return {
            ...result,
            DisplayMessage: message,
          };
        }
        if (payload?.IsDeleted === true) continue;
        const savedRaw = result?.Items?.first ? result.Items.first() : payload;
        if (savedRaw) savedLoops.push(normalizeLoopMap(savedRaw, eoId, sourceId));
      }
    } catch (ex) {
      const message = ex?.message || "Loop mappings could not be saved.";
      setError(message);
      return { Success: false, DisplayMessage: message };
    }

    setError(null);
    setInitialLoopMapsBySourceId((previous) => ({ ...previous, [sourceId]: savedLoops }));
    return { Success: true, loops: savedLoops };
  };

  const handleEntityFieldUpdate = async (fields, entityObjectRawPatch = null) => {
    const normalizedFields = asArray(fields);
    const currentRaw = entityObjectRawPatch || entityObject?.raw || {};
    const payload = {
      ...currentRaw,
      _id: currentRaw?._id || currentRaw?.UniqueId || currentRaw?.Id || currentRaw?.EntityObjectId || eoId,
      EntityObjectId: currentRaw?.EntityObjectId || eoId,
      Fields: normalizedFields.map((field, index) => toEntityFieldSaveModel(field, index)),
    };

    const result = await entityObjectMgr.saveItem(payload);
    if (!result?.Success) return result || { Success: false };

    const savedRaw = result?.Items?.first ? result.Items.first() : payload;
    const normalizedEntity = normalizeEntityObject(savedRaw);
    setEntityObject(normalizedEntity);
    return { Success: true, fields: normalizedEntity.fields };
  };

  const handleAiSuggestForField = async ({ entityObject: entity, sourceObject, sourceField, currentMap }) => {
    try {
      const result = await entityObjectMgr.RunOperation({
        operationName: aiFieldOp,
        id: entity?.id,
        queryParams: {
          sourceobjectid: sourceObject?.id,
          sourceobjectfieldid: sourceField?.id,
        },
        item: {
          sourceobjectid: sourceObject?.id,
          sourceobjectfieldid: sourceField?.id,
        },
        isItem: true,
      });
      if (!result?.Success) {
        return {
          Success: false,
          error: operationErrorMessage(result, "AI field mapping failed."),
        };
      }
      const resultItem = readAiResultItem(result);
      const normalized = flattenAiMapsToItems(resultItem, entity?.id).filter((x) => x.sourceFieldId === sourceField?.id);
      if (normalized.length === 0) {
        return {
          Success: false,
          error: String(resultItem?.Comment || resultItem?.comment || "AI did not return valid mappings for the selected field."),
        };
      }
      const chosen = normalized[0];
      const kept = asArray(currentMap?.items).filter((x) => x.sourceFieldId !== chosen.sourceFieldId && x.entityFieldId !== chosen.entityFieldId);
      const warnings = [];
      const comment = String(resultItem?.Comment || resultItem?.comment || "").trim();
      if (comment) warnings.push(comment);
      return { Success: true, items: [...kept, chosen], warnings };
    } catch {
      return {
        Success: false,
        error: "AI field mapping request failed.",
      };
    }
  };

  const handleAiSuggestForEntityField = async ({ entityObject: entity, sourceObject, entityField, currentMap }) => {
    try {
      const result = await entityObjectMgr.RunOperation({
        operationName: aiFieldOp,
        id: entity?.id,
        queryParams: {
          sourceobjectid: sourceObject?.id,
          entityobjectfieldid: entityField?.id,
        },
        item: {
          sourceobjectid: sourceObject?.id,
          entityobjectfieldid: entityField?.id,
        },
        isItem: true,
      });
      if (!result?.Success) {
        return {
          Success: false,
          error: operationErrorMessage(result, "AI field mapping failed."),
        };
      }

      const resultItem = readAiResultItem(result);
      const normalized = flattenAiMapsToItems(resultItem, entity?.id).filter((x) => x.entityFieldId === entityField?.id);
      if (normalized.length === 0) {
        return {
          Success: false,
          error: String(resultItem?.Comment || resultItem?.comment || "AI did not return valid mappings for the selected field."),
        };
      }

      const chosen = normalized[0];
      const kept = asArray(currentMap?.items).filter((x) => x.entityFieldId !== chosen.entityFieldId);
      const warnings = [];
      const comment = String(resultItem?.Comment || resultItem?.comment || "").trim();
      if (comment) warnings.push(comment);
      return { Success: true, items: [...kept, chosen], warnings };
    } catch {
      return {
        Success: false,
        error: "AI field mapping request failed.",
      };
    }
  };

  const handleAiBuildTransform = async ({ prompt, currentScript }) => {
    const text = String(prompt || "").trim();
    if (!text) {
      return {
        Success: false,
        error: "Describe the transform to generate.",
      };
    }

    const currentFieldValue = String(currentScript || "");
    try {
      const result = await entityObjectMgr.RunOperation({
        operationName: aiTransformOp,
        id: entityObject?.id || eoId,
        queryParams: {
          v: text,
          fv: currentFieldValue,
        },
        item: {
          v: text,
          fv: currentFieldValue,
        },
        isItem: true,
      });

      if (!result?.Success) {
        return {
          Success: false,
          error: operationErrorMessage(result, "AI transform generation failed."),
        };
      }

      const item = firstItem(result?.Items) || firstItem(result?.Value?.Items);
      const generated =
        (typeof result?.Value === "string" ? result.Value : "") ||
        (typeof item === "string" ? item : "") ||
        item?.Value ||
        item?.value ||
        item?.Output ||
        item?.output ||
        item?.CustomTransformScript ||
        "";

      if (!String(generated || "").trim()) {
        return {
          Success: false,
          error: "AI did not return transform code.",
        };
      }

      return {
        Success: true,
        code: String(generated),
      };
    } catch {
      return {
        Success: false,
        error: "AI transform generation request failed.",
      };
    }
  };

  if (loading) {
    return (
      <div className="centered-loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return <div className="entity-field-map-screen__error">{error}</div>;
  }

  const effectiveAllowMultiSource = allowMultiSource || sourceObjects.length > 1;
  const handleBack = () => {
    if (useHardBackRedirect && hardRedirectTo(backUrl)) return;
    navigate(backUrl);
  };

  return (
    <div className="entity-field-map-screen">
      <div className="entity-field-map-screen__header">
        <h2 className="entity-field-map-screen__title">
          <IconControl iconKey={"schema"} /> Entity Object Designer
        </h2>
        <button type="button" className="entity-field-map-screen__back" onClick={handleBack}>
          {"<<"} Back
        </button>
      </div>
      {aiStatus ? <div className="entity-field-map-screen__ai-status">{aiStatus}</div> : null}
      <AIPanel
        open={true}
        presentation="inline"
        mapToClass="core-layout"
        params={{
          entityObjectId: eoId,
          sourceObjectId: aiMappingContext?.activeSourceId || aiMappingContext?.sourceObjectId || "",
        }}
        payload={aiMappingContext}
        referenceId={eoId}
        referenceName="EntityFieldMapping"
        session={selectedChatSession}
        operationName="AIQuestionEntityFieldMapping"
        introMessage="Manage Field Mapping..."
        splashMessage="How can I help you with this entity mapping?"
        defaultOrchestrationMode="AgentAssist"
        singleSessionMode={true}
        planningPreviewEnabled={false}
        onSessionChange={handleAiSessionChange}
        onUpdatePanel={handleAiPanelUpdate}
        onCancel={handleBack}
      >
        <EntityFieldMapDesigner
          entityObject={entityObject}
          sourceObjects={sourceObjects}
          initialMapsBySourceId={initialMapsBySourceId}
          initialLoopMapsBySourceId={initialLoopMapsBySourceId}
          dataTypes={dataTypes}
          transformCatalog={transformCatalog}
          fieldItemMetadata={fieldItemMetadata}
          entityObjectItemMetadata={entityObjectItemMetadata}
          sourceObjectFieldMapItemMetadata={sourceObjectFieldMapItemMetadata}
          sourceObjectFieldTransformScriptItemMetadata={sourceObjectFieldTransformScriptItemMetadata}
          lookupSourceObjects={lookupSourceObjects}
          lookupEntityObjects={lookupEntityObjects}
          allowMultiSource={effectiveAllowMultiSource}
          aiDraftUpdate={aiDraftUpdate}
          onAiContextChange={handleAiContextChange}
          onSaveMap={handleSaveMap}
          onSaveLoops={handleSaveLoops}
          onEntityFieldUpdate={handleEntityFieldUpdate}
          onAiSuggestForField={handleAiSuggestForField}
          onAiSuggestForEntityField={handleAiSuggestForEntityField}
          onAiBuildTransform={handleAiBuildTransform}
        />
      </AIPanel>
    </div>
  );
};

export default EntityFieldMapDesignerManage;
